const fmt = (n) => n.toLocaleString();

const webDevelopment = {
  serviceName: "Web Development",
  greeting:
    "Great choice! Let me build a custom estimate for your web project. I'll ask a few quick questions.",
  steps: [
    {
      key: "websiteType",
      question: "What type of website do you need?",
      options: [
        { label: "Landing Page", value: "landing" },
        { label: "Business Site", value: "business" },
        { label: "E-commerce", value: "ecommerce" },
        { label: "Web Application", value: "webapp" },
        { label: "Portfolio", value: "portfolio" },
        { label: "Blog / News", value: "blog" },
      ],
    },
    {
      key: "pages",
      question: "Approximately how many pages?",
      options: [
        { label: "1-5 pages", value: "1-5" },
        { label: "6-15 pages", value: "6-15" },
        { label: "16-30 pages", value: "16-30" },
        { label: "30+ pages", value: "30+" },
      ],
    },
    {
      key: "domain",
      question: "Do you need a domain name?",
      options: [
        { label: "Yes, register new", value: "new" },
        { label: "I already have one", value: "existing" },
      ],
    },
    {
      key: "hosting",
      question: "Do you need hosting?",
      options: [
        { label: "Yes, set it up for me", value: "new" },
        { label: "I already have hosting", value: "existing" },
      ],
    },
    {
      key: "seo",
      question: "Do you need SEO optimization?",
      options: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ],
    },
  ],
  calculate(answers) {
    const basePrices = {
      landing: [15000, 35000],
      business: [50000, 120000],
      ecommerce: [100000, 400000],
      webapp: [200000, 800000],
      portfolio: [20000, 55000],
      blog: [30000, 70000],
    };
    const [baseMin, baseMax] = basePrices[answers.websiteType] || [50000, 120000];

    const pageMultiplier = { "1-5": 0, "6-15": 0.3, "16-30": 0.6, "30+": 1.0 };
    const pagesAdd = pageMultiplier[answers.pages] || 0;

    const domainCost = answers.domain === "new" ? [2500, 4000] : [0, 0];
    const hostingCost = answers.hosting === "new" ? [8000, 25000] : [0, 0];
    const seoCost = answers.seo === "yes" ? [25000, 80000] : [0, 0];

    const min = Math.round(
      (baseMin * (1 + pagesAdd)) + domainCost[0] + hostingCost[0] + seoCost[0]
    );
    const max = Math.round(
      (baseMax * (1 + pagesAdd)) + domainCost[1] + hostingCost[1] + seoCost[1]
    );

    const breakdown = [
      `${answers.websiteType.charAt(0).toUpperCase() + answers.websiteType.slice(1)} website: KES ${fmt(baseMin)} – ${fmt(baseMax)}`,
      `${answers.pages} pages: +${Math.round(pagesAdd * 100)}%`,
    ];
    if (answers.domain === "new") breakdown.push(`Domain registration: KES ${fmt(domainCost[0])} – ${fmt(domainCost[1])}`);
    if (answers.hosting === "new") breakdown.push(`Hosting (1 year): KES ${fmt(hostingCost[0])} – ${fmt(hostingCost[1])}`);
    if (answers.seo === "yes") breakdown.push(`SEO optimization: KES ${fmt(seoCost[0])} – ${fmt(seoCost[1])}`);

    return { min, max, breakdown };
  },
};

const cybersecurity = {
  serviceName: "Cybersecurity Consultancy",
  greeting:
    "Excellent! Let me put together an estimate for your security needs.",
  steps: [
    {
      key: "scope",
      question: "What do you need?",
      options: [
        { label: "Penetration Test", value: "pentest" },
        { label: "SIEM Deployment", value: "siem" },
        { label: "Compliance Audit", value: "compliance" },
        { label: "Full Security Assessment", value: "full" },
      ],
    },
    {
      key: "assets",
      question: "How many assets (servers, endpoints, IPs) need coverage?",
      options: [
        { label: "1-10 (Small)", value: "small" },
        { label: "11-50 (Medium)", value: "medium" },
        { label: "50-200 (Large)", value: "large" },
        { label: "200+ (Enterprise)", value: "enterprise" },
      ],
    },
    {
      key: "industry",
      question: "What industry is your organisation in?",
      options: [
        { label: "Technology / SaaS", value: "tech" },
        { label: "Finance / Banking", value: "finance" },
        { label: "Healthcare", value: "health" },
        { label: "Retail / E-commerce", value: "retail" },
        { label: "Other", value: "other" },
      ],
    },
    {
      key: "compliance",
      question: "Any compliance frameworks you need to meet?",
      options: [
        { label: "None", value: "none" },
        { label: "ISO 27001", value: "iso" },
        { label: "PCI-DSS", value: "pci" },
        { label: "Not sure yet", value: "unsure" },
      ],
    },
  ],
  calculate(answers) {
    const basePrices = {
      pentest: [60000, 180000],
      siem: [120000, 450000],
      compliance: [100000, 350000],
      full: [200000, 700000],
    };
    const [baseMin, baseMax] = basePrices[answers.scope] || [100000, 300000];

    const sizeMultiplier = { small: 0, medium: 0.25, large: 0.6, enterprise: 1.0 };
    const sizeAdd = sizeMultiplier[answers.assets] || 0;

    const complianceCost =
      answers.compliance === "none" ? [0, 0] :
      answers.compliance === "unsure" ? [15000, 40000] :
      [40000, 120000];

    const industryMultiplier = { finance: 0.2, tech: 0, health: 0.15, retail: 0, other: 0 };
    const industryAdd = industryMultiplier[answers.industry] || 0;

    const min = Math.round((baseMin * (1 + sizeAdd + industryAdd)) + complianceCost[0]);
    const max = Math.round((baseMax * (1 + sizeAdd + industryAdd)) + complianceCost[1]);

    const breakdown = [
      `${answers.scope === "pentest" ? "Penetration testing" : answers.scope === "siem" ? "SIEM deployment" : answers.scope === "compliance" ? "Compliance audit" : "Full security assessment"}: KES ${fmt(baseMin)} – ${fmt(baseMax)}`,
      `Asset scale (${answers.assets}): +${Math.round((sizeAdd + industryAdd) * 100)}%`,
    ];
    if (answers.compliance !== "none") breakdown.push(`Compliance (${answers.compliance}): KES ${fmt(complianceCost[0])} – ${fmt(complianceCost[1])}`);

    return { min, max, breakdown };
  },
};

const networkDesign = {
  serviceName: "Network Design & Engineering",
  greeting:
    "Let me build an estimate for your network project.",
  steps: [
    {
      key: "type",
      question: "What type of network do you need?",
      options: [
        { label: "LAN (Local)", value: "lan" },
        { label: "WAN (Wide)", value: "wan" },
        { label: "Both LAN + WAN", value: "both" },
      ],
    },
    {
      key: "locations",
      question: "How many locations / sites?",
      options: [
        { label: "1 site", value: "1" },
        { label: "2-5 sites", value: "2-5" },
        { label: "5+ sites", value: "5+" },
      ],
    },
    {
      key: "devices",
      question: "How many devices / users?",
      options: [
        { label: "Under 20", value: "small" },
        { label: "20-100", value: "medium" },
        { label: "100-500", value: "large" },
        { label: "500+", value: "enterprise" },
      ],
    },
    {
      key: "setup",
      question: "Is this a new setup or an upgrade?",
      options: [
        { label: "Brand new setup", value: "new" },
        { label: "Upgrade existing", value: "upgrade" },
        { label: "Expansion", value: "expansion" },
      ],
    },
  ],
  calculate(answers) {
    const basePrices = { lan: [80000, 250000], wan: [150000, 500000], both: [200000, 650000] };
    const [baseMin, baseMax] = basePrices[answers.type] || [100000, 300000];

    const locMultiplier = { "1": 0, "2-5": 0.4, "5+": 0.9 };
    const locAdd = locMultiplier[answers.locations] || 0;

    const deviceMultiplier = { small: 0, medium: 0.2, large: 0.5, enterprise: 0.8 };
    const devAdd = deviceMultiplier[answers.devices] || 0;

    const setupMultiplier = { new: 1, upgrade: 0.6, expansion: 0.5 };
    const setupFactor = setupMultiplier[answers.setup] || 1;

    const min = Math.round((baseMin + baseMin * (locAdd + devAdd)) * setupFactor);
    const max = Math.round((baseMax + baseMax * (locAdd + devAdd)) * setupFactor);

    const typeLabel = answers.type === "lan" ? "LAN design" : answers.type === "wan" ? "WAN design" : "LAN + WAN design";
    const breakdown = [
      `${typeLabel}: KES ${fmt(baseMin)} – ${fmt(baseMax)}`,
      `${answers.locations} location(s): +${Math.round(locAdd * 100)}%`,
      `Device scale (${answers.devices}): +${Math.round(devAdd * 100)}%`,
    ];
    if (answers.setup !== "new") breakdown.push(`Setup type (${answers.setup}): ${answers.setup === "upgrade" ? "-40%" : "-50%"}`);

    return { min, max, breakdown };
  },
};

const ispInfrastructure = {
  serviceName: "ISP Infrastructure Solutions",
  greeting:
    "Let me estimate the investment for your ISP infrastructure.",
  steps: [
    {
      key: "subscribers",
      question: "What's your target subscriber count?",
      options: [
        { label: "Under 100", value: "small" },
        { label: "100-500", value: "medium" },
        { label: "500-1,000", value: "large" },
        { label: "1,000+", value: "enterprise" },
      ],
    },
    {
      key: "technology",
      question: "What delivery technology?",
      options: [
        { label: "Fiber (GPON)", value: "fiber" },
        { label: "Wireless (PtP/PtMP)", value: "wireless" },
        { label: "Both", value: "both" },
      ],
    },
    {
      key: "coverage",
      question: "What's your coverage area?",
      options: [
        { label: "Single location / estate", value: "single" },
        { label: "Multi-site / campus", value: "multi" },
        { label: "Town / district", value: "regional" },
      ],
    },
  ],
  calculate(answers) {
    const basePrices = { small: [500000, 1500000], medium: [1500000, 4000000], large: [3500000, 8000000], enterprise: [7000000, 18000000] };
    const [baseMin, baseMax] = basePrices[answers.subscribers] || [1000000, 3000000];

    const techMultiplier = { fiber: 1, wireless: 0.7, both: 1.3 };
    const techFactor = techMultiplier[answers.technology] || 1;

    const coverageMultiplier = { single: 0, multi: 0.3, regional: 0.7 };
    const covAdd = coverageMultiplier[answers.coverage] || 0;

    const min = Math.round(baseMin * techFactor * (1 + covAdd));
    const max = Math.round(baseMax * techFactor * (1 + covAdd));

    const techLabel = answers.technology === "fiber" ? "Fiber (GPON)" : answers.technology === "wireless" ? "Wireless" : "Fiber + Wireless";
    const breakdown = [
      `${techLabel} for ${answers.subscribers} subscribers: KES ${fmt(baseMin)} – ${fmt(baseMax)}`,
      `Coverage (${answers.coverage}): +${Math.round(covAdd * 100)}%`,
    ];

    return { min, max, breakdown };
  },
};

const cctv = {
  serviceName: "CCTV Installation & Monitoring",
  greeting:
    "Let me put together an estimate for your surveillance system.",
  steps: [
    {
      key: "cameras",
      question: "How many cameras do you need?",
      options: [
        { label: "1-4 cameras", value: "1-4" },
        { label: "5-16 cameras", value: "5-16" },
        { label: "17-32 cameras", value: "17-32" },
        { label: "32+ cameras", value: "32+" },
      ],
    },
    {
      key: "type",
      question: "What camera type?",
      options: [
        { label: "Indoor only", value: "indoor" },
        { label: "Outdoor only", value: "outdoor" },
        { label: "Both indoor + outdoor", value: "both" },
      ],
    },
    {
      key: "storage",
      question: "How long do you need to retain footage?",
      options: [
        { label: "30 days", value: "30" },
        { label: "60 days", value: "60" },
        { label: "90 days", value: "90" },
        { label: "90+ days", value: "90+" },
      ],
    },
    {
      key: "remote",
      question: "Do you need remote mobile viewing?",
      options: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ],
    },
  ],
  calculate(answers) {
    const cameraPrices = { indoor: [12000, 30000], outdoor: [18000, 45000], both: [15000, 38000] };
    const counts = { "1-4": 3, "5-16": 10, "17-32": 25, "32+": 40 };
    const [perMin, perMax] = cameraPrices[answers.type] || [15000, 35000];
    const camCount = counts[answers.cameras] || 10;

    const camMin = perMin * camCount;
    const camMax = perMax * camCount;

    const nvrCost = camCount <= 8 ? [25000, 60000] : camCount <= 16 ? [50000, 120000] : [100000, 250000];

    const storageMultiplier = { "30": 0, "60": 0.15, "90": 0.3, "90+": 0.45 };
    const storageAdd = storageMultiplier[answers.storage] || 0;

    const remoteCost = answers.remote === "yes" ? [20000, 50000] : [0, 0];

    const installCost = Math.round((camMin + camMax) / 2 * 0.15);

    const min = Math.round(camMin + nvrCost[0] + camMin * storageAdd + remoteCost[0] + installCost * 0.8);
    const max = Math.round(camMax + nvrCost[1] + camMax * storageAdd + remoteCost[1] + installCost * 1.2);

    const breakdown = [
      `${camCount} × ${answers.type} cameras: KES ${fmt(camMin)} – ${fmt(camMax)}`,
      `NVR system: KES ${fmt(nvrCost[0])} – ${fmt(nvrCost[1])}`,
      `Installation: ~KES ${fmt(Math.round(installCost * 0.8))} – ${fmt(Math.round(installCost * 1.2))}`,
    ];
    if (storageAdd > 0) breakdown.push(`Extended storage (${answers.storage} days): +${Math.round(storageAdd * 100)}%`);
    if (answers.remote === "yes") breakdown.push(`Remote viewing setup: KES ${fmt(remoteCost[0])} – ${fmt(remoteCost[1])}`);

    return { min, max, breakdown };
  },
};

const database = {
  serviceName: "Database Management",
  greeting:
    "Let me estimate the scope of your database project.",
  steps: [
    {
      key: "projectType",
      question: "What do you need?",
      options: [
        { label: "New database design", value: "new" },
        { label: "Migration from another system", value: "migration" },
        { label: "Performance optimisation", value: "optimise" },
        { label: "Ongoing maintenance", value: "maintenance" },
      ],
    },
    {
      key: "size",
      question: "What's the expected database size?",
      options: [
        { label: "Small (< 10 GB)", value: "small" },
        { label: "Medium (10-100 GB)", value: "medium" },
        { label: "Large (100 GB - 1 TB)", value: "large" },
        { label: "Enterprise (1 TB+)", value: "enterprise" },
      ],
    },
    {
      key: "complexity",
      question: "How complex are the requirements?",
      options: [
        { label: "Simple CRUD operations", value: "simple" },
        { label: "Complex queries & analytics", value: "complex" },
        { label: "Enterprise-grade (HA, replication)", value: "enterprise" },
      ],
    },
  ],
  calculate(answers) {
    const basePrices = {
      new: [80000, 280000],
      migration: [100000, 400000],
      optimise: [50000, 200000],
      maintenance: [20000, 60000],
    };
    const [baseMin, baseMax] = basePrices[answers.projectType] || [60000, 200000];

    const sizeMultiplier = { small: 0, medium: 0.3, large: 0.7, enterprise: 1.2 };
    const sizeAdd = sizeMultiplier[answers.size] || 0;

    const complexityMultiplier = { simple: 0, complex: 0.3, enterprise: 0.7 };
    const cplxAdd = complexityMultiplier[answers.complexity] || 0;

    const isMaintenance = answers.projectType === "maintenance";
    const min = Math.round((baseMin * (1 + sizeAdd + cplxAdd)) * (isMaintenance ? 1 : 1));
    const max = Math.round((baseMax * (1 + sizeAdd + cplxAdd)) * (isMaintenance ? 1 : 1));
    const period = isMaintenance ? "/month" : "";

    const breakdown = [
      `${answers.projectType === "new" ? "New design" : answers.projectType === "migration" ? "Migration" : answers.projectType === "optimise" ? "Optimisation" : "Monthly maintenance"}: KES ${fmt(baseMin)}${period} – ${fmt(baseMax)}${period}`,
      `Data scale (${answers.size}): +${Math.round(sizeAdd * 100)}%`,
    ];
    if (answers.complexity !== "simple") breakdown.push(`Complexity (${answers.complexity}): +${Math.round(cplxAdd * 100)}%`);

    return { min, max, breakdown, period };
  },
};

const spatialPlanning = {
  serviceName: "Technical Spatial Planning",
  greeting:
    "Let me build an estimate for your spatial planning project.",
  steps: [
    {
      key: "projectType",
      question: "What type of project?",
      options: [
        { label: "Residential", value: "residential" },
        { label: "Commercial", value: "commercial" },
        { label: "Container cabin", value: "container" },
        { label: "Industrial", value: "industrial" },
      ],
    },
    {
      key: "floors",
      question: "How many floors?",
      options: [
        { label: "1 floor", value: "1" },
        { label: "2 floors", value: "2" },
        { label: "3+ floors", value: "3+" },
      ],
    },
    {
      key: "requirements",
      question: "Any special requirements?",
      options: [
        { label: "Standard", value: "standard" },
        { label: "Accessibility compliant", value: "accessibility" },
        { label: "Green / sustainable design", value: "green" },
        { label: "Custom structural specs", value: "custom" },
      ],
    },
  ],
  calculate(answers) {
    const basePrices = {
      residential: [35000, 90000],
      commercial: [80000, 280000],
      container: [20000, 55000],
      industrial: [100000, 350000],
    };
    const [baseMin, baseMax] = basePrices[answers.projectType] || [50000, 150000];

    const floorMultiplier = { "1": 0, "2": 0.35, "3+": 0.7 };
    const floorAdd = floorMultiplier[answers.floors] || 0;

    const reqCost =
      answers.requirements === "standard" ? [0, 0] :
      answers.requirements === "accessibility" ? [15000, 40000] :
      answers.requirements === "green" ? [20000, 60000] :
      [30000, 80000];

    const min = Math.round(baseMin * (1 + floorAdd) + reqCost[0]);
    const max = Math.round(baseMax * (1 + floorAdd) + reqCost[1]);

    const typeLabel = answers.projectType.charAt(0).toUpperCase() + answers.projectType.slice(1);
    const breakdown = [
      `${typeLabel} floor plan: KES ${fmt(baseMin)} – ${fmt(baseMax)}`,
      `${answers.floors} floor(s): +${Math.round(floorAdd * 100)}%`,
    ];
    if (answers.requirements !== "standard") breakdown.push(`Special requirements (${answers.requirements}): KES ${fmt(reqCost[0])} – ${fmt(reqCost[1])}`);

    return { min, max, breakdown };
  },
};

const itConsulting = {
  serviceName: "IT Consulting & Automation",
  greeting:
    "Let me estimate the scope of your consulting project.",
  steps: [
    {
      key: "consultingType",
      question: "What type of consulting?",
      options: [
        { label: "IT Audit & Assessment", value: "audit" },
        { label: "Technology Strategy", value: "strategy" },
        { label: "Network Automation", value: "automation" },
        { label: "DevOps & CI/CD Setup", value: "devops" },
      ],
    },
    {
      key: "companySize",
      question: "How large is your organisation?",
      options: [
        { label: "1-10 employees", value: "small" },
        { label: "11-50 employees", value: "medium" },
        { label: "50-200 employees", value: "large" },
        { label: "200+", value: "enterprise" },
      ],
    },
    {
      key: "maturity",
      question: "What's your current tech maturity?",
      options: [
        { label: "Starting out", value: "starting" },
        { label: "Some tools in place", value: "some" },
        { label: "Mature infrastructure", value: "mature" },
      ],
    },
    {
      key: "timeline",
      question: "What's your timeline?",
      options: [
        { label: "Standard (4-8 weeks)", value: "standard" },
        { label: "Urgent (1-2 weeks)", value: "rush" },
      ],
    },
  ],
  calculate(answers) {
    const basePrices = {
      audit: [60000, 200000],
      strategy: [100000, 350000],
      automation: [80000, 300000],
      devops: [150000, 500000],
    };
    const [baseMin, baseMax] = basePrices[answers.consultingType] || [80000, 250000];

    const sizeMultiplier = { small: 0, medium: 0.2, large: 0.5, enterprise: 0.9 };
    const sizeAdd = sizeMultiplier[answers.companySize] || 0;

    const maturityFactor = { starting: 1.1, some: 1, mature: 0.85 };
    const matFactor = maturityFactor[answers.maturity] || 1;

    const rushFactor = answers.timeline === "rush" ? 1.3 : 1;

    const min = Math.round(baseMin * (1 + sizeAdd) * matFactor * rushFactor);
    const max = Math.round(baseMax * (1 + sizeAdd) * matFactor * rushFactor);

    const typeLabel =
      answers.consultingType === "audit" ? "IT Audit & Assessment" :
      answers.consultingType === "strategy" ? "Technology Strategy" :
      answers.consultingType === "automation" ? "Network Automation" :
      "DevOps & CI/CD Setup";

    const breakdown = [
      `${typeLabel}: KES ${fmt(baseMin)} – ${fmt(baseMax)}`,
      `Company size (${answers.companySize}): +${Math.round(sizeAdd * 100)}%`,
    ];
    if (answers.maturity !== "some") breakdown.push(`Tech maturity (${answers.maturity}): ${answers.maturity === "starting" ? "+10%" : "-15%"}`);
    if (answers.timeline === "rush") breakdown.push("Rush delivery: +30%");

    return { min, max, breakdown };
  },
};

const quoteFlows = [
  webDevelopment,
  cybersecurity,
  networkDesign,
  ispInfrastructure,
  cctv,
  database,
  spatialPlanning,
  itConsulting,
];

export function getQuoteFlow(serviceName) {
  return quoteFlows.find(
    (f) => f.serviceName.toLowerCase() === serviceName.toLowerCase()
  );
}

export function getServiceNames() {
  return quoteFlows.map((f) => f.serviceName);
}

export function matchServiceOption(input, options) {
  const normalised = input.toLowerCase().trim();

  for (const opt of options) {
    if (opt.value.toLowerCase() === normalised) return opt;
    if (opt.label.toLowerCase() === normalised) return opt;
    if (opt.label.toLowerCase().includes(normalised) && normalised.length >= 3) return opt;
  }

  for (const opt of options) {
    const labelTokens = opt.label.toLowerCase().split(/\s+/);
    const inputTokens = normalised.split(/\s+/);
    for (const t of inputTokens) {
      if (labelTokens.some((lt) => lt.startsWith(t) && t.length >= 3)) return opt;
    }
  }

  return null;
}

export function formatQuoteSummary(serviceName, estimate) {
  const lines = estimate.breakdown.map((b) => `  • ${b}`).join("\n");
  const period = estimate.period || "";
  return (
    `Here's your estimated quote for ${serviceName}:\n\n` +
    `Project Breakdown:\n${lines}\n\n` +
    `Estimated Range: KES ${fmt(estimate.min)}${period} – ${fmt(estimate.max)}${period}\n\n` +
    `This is an indicative estimate based on your answers. Our team will prepare a detailed, fixed-price proposal after a brief consultation.\n\n` +
    `Would you like this estimate emailed to you?`
  );
}
